import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { NavbarDashbord } from './navbarDashbord';
import { updateClimatisseur, deleteComment, updateComment, addComment } from './action';
import { Trash2, CheckCircle, MessageSquare, Plus, User, Heart, MessageCircle, X, ShoppingCart, Star } from 'lucide-react';
import { SupprimerCommentaire, ModifierStatutCommentaire, AjouterCommentaireAdmin } from './slice';
import '../styles/admin.css';
import API_BASE_URL from '../config';

export function PageCommentaires() {
    const listC = useSelector(state => state.listC) || [];
    const comptes = useSelector(state => state.comptes) || [];
    const dispatch = useDispatch();
    const location = useLocation();
    
    const loggedUserStr = localStorage.getItem('user');
    const loggedUser = loggedUserStr ? JSON.parse(loggedUserStr) : null;

    // States
    const [allComments, setAllComments] = useState([]);
    const [highlightId, setHighlightId] = useState(null);
    const [filterProduct, setFilterProduct] = useState('Tous les produits');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    
    // Confirmation delete modal
    const [confirmDeleteComment, setConfirmDeleteComment] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newAdminComment, setNewAdminComment] = useState({ 
        climatiseurId: '', 
        username: loggedUser?.name || 'Admin Jamas', 
        avis: '5', 
        commantaire: '' 
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cid = params.get('climatiseurId');
        
        let flattened = listC.flatMap(c => 
            (c.commantaires || []).map(com => ({
                ...com,
                climatiseurId: c.id,
                climatiseurNom: c.nomClimatisseur
            }))
        );

        if (cid) {
            flattened = flattened.filter(com => com.climatiseurId == cid);
        }

        // Sort by ID descending (newest first)
        flattened.sort((a, b) => (parseInt(b.id) || 0) - (parseInt(a.id) || 0));

        setAllComments(flattened);

        const highlight = params.get('highlight');
        if (highlight) {
            const hId = parseInt(highlight);
            setHighlightId(hId);
            setTimeout(() => {
                const el = document.getElementById(`comment-${hId}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
        }
    }, [listC, location]);

    const filteredComments = allComments.filter(com => {
        if (filterProduct !== 'Tous les produits' && com.climatiseurNom !== filterProduct) return false;
        return true;
    });

    const handleSupprimer = (climatiseurId, commentaireId) => {
        const com = allComments.find(c => c.id === commentaireId);
        setConfirmDeleteComment(com || { id: commentaireId, username: "l'utilisateur", climatiseurNom: "ce produit" });
        setDeleteError('');
    };

    const confirmDeleteAction = () => {
        if (confirmDeleteComment) {
            const commentId = parseInt(confirmDeleteComment.id);
            
            // Validation: ID must be valid
            if (!commentId || isNaN(commentId)) {
                setDeleteError("Impossible de supprimer ce commentaire : identifiant invalide.");
                return;
            }

            setDeleteError('');
            dispatch(deleteComment(commentId))
                .unwrap()
                .then(() => {
                    setConfirmDeleteComment(null);
                })
                .catch((err) => {
                    console.error("Delete comment error:", err);
                    const msg = err?.response?.data?.message || err?.message || "Erreur lors de la suppression.";
                    setDeleteError(`Erreur: ${msg}`);
                });
        }
    };

    const handleValider = (climatiseurId, commentaireId, currentStatus) => {
        const nextStatus = currentStatus === 'validé' ? 'en attente' : 'validé';
        dispatch(updateComment({ id: commentaireId, status: nextStatus, climatiseur_id: climatiseurId }))
            .unwrap()
            .then(() => alert("Commentaire validé avec succès !"))
            .catch(() => alert("Erreur lors de la validation du commentaire."));
    };

    const handleAddComment = (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const cid = parseInt(newAdminComment.climatiseurId);
        if (!cid) return alert("Veuillez choisir un produit.");
        
        setIsSubmitting(true);
        const starMap = {
            '5': '5 étoiles',
            '4': '4 étoiles',
            '3': '3 étoiles',
            '2': '2 étoiles',
            '1': '1 étoile'
        };

        const newCom = {
            climatiseur_id: cid,
            username: newAdminComment.username,
            avis: starMap[newAdminComment.avis] || `${newAdminComment.avis} étoiles`,
            commantaire: newAdminComment.commantaire,
            status: 'validé'
        };

        dispatch(addComment(newCom))
            .unwrap()
            .then((addedComment) => {
                // Dispatch action to update state immediately
                dispatch(AjouterCommentaireAdmin({ climatiseurId: cid, newComment: addedComment }));
                
                setNewAdminComment({ ...newAdminComment, climatiseurId: '', commantaire: '' });
                setIsAddModalOpen(false);
            })
            .catch(err => {
                console.error("Failed to add comment:", err);
                alert("Erreur lors de l'ajout du commentaire.");
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Modération des Commentaires</h1>
                        <p>Gérez les retours et avis de vos clients ({filteredComments.length} affichés).</p>
                    </div>
                </header>

                <div className="admin-table-container" style={{ marginTop: '20px' }}>
                    <div className="table-header-actions" style={{ flexWrap: 'wrap', gap: '15px' }}>
                        <div className="admin-filters">
                            <select 
                                className="admin-select" 
                                value={filterProduct} 
                                onChange={e => setFilterProduct(e.target.value)}
                            >
                                <option>Tous les produits</option>
                                {listC.map(c => <option key={c.id} value={c.nomClimatisseur}>{c.nomClimatisseur}</option>)}
                            </select>
                        </div>
                        <button className="admin-btn-add" onClick={() => setIsAddModalOpen(true)}>
                            <Plus size={18} />
                            <span className="btn-text-full">Répondre / Ajouter</span>
                            <span className="btn-text-short">R / A</span>
                        </button>
                    </div>

                    <div className="comments-grid" style={{ display: 'grid', gap: '20px', marginTop: '20px' }}>
                        {filteredComments.length > 0 ? (
                            filteredComments.map((com) => (
                                <div 
                                    key={`${com.climatiseurId}-${com.id}`} 
                                    className={`comment-admin-card ${highlightId === com.id ? 'highlighted' : ''}`}
                                    id={`comment-${com.id}`}
                                    style={{
                                        background: 'white',
                                        padding: '24px',
                                        borderRadius: '20px',
                                        border: highlightId === com.id ? '2px solid var(--admin-secondary)' : '1px solid var(--admin-border)',
                                        boxShadow: highlightId === com.id ? '0 0 30px rgba(112, 0, 0, 0.15)' : 'var(--admin-shadow)',
                                        transition: 'all 0.3s ease',
                                        position: 'relative'
                                    }}
                                >
                                    <div className="comment-card-top-row" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                        <div className="comment-user-info-group" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                            <div className="comment-avatar-wrapper" style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-primary)', border: '1px solid var(--admin-border)', overflow: 'hidden', flexShrink: 0 }}>
                                                {(() => {
                                                    const commentUser = (comptes || []).find(u => u.username === com.username || u.email.split('@')[0] === com.username);
                                                    if (commentUser?.image_user) {
                                                        return <img src={commentUser.image_user?.startsWith('data:image') ? commentUser.image_user : `${API_BASE_URL.replace('/api', '')}/storage/photos/${commentUser.image_user}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
                                                    }
                                                    return (
                                                         <span style={{ fontWeight: '800', fontSize: '1.2rem', color: 'var(--admin-secondary)', textTransform: 'uppercase' }}>
                                                             {com.username ? com.username.charAt(0) : 'U'}
                                                         </span>
                                                     );
                                                })()}
                                            </div>
                                            <div className="comment-user-text-details">
                                                <div className="comment-username-line" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <h4 className="comment-username-text" style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800' }}>{com.username}</h4>
                                                    <span className="comment-status-badge desktop-status-badge" style={{ 
                                                        fontSize: '0.65rem', 
                                                        fontWeight: '900', 
                                                        padding: '3px 8px', 
                                                        borderRadius: '6px',
                                                        background: com.status === 'validé' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                                        color: com.status === 'validé' ? '#166534' : '#9a3412',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {com.status === 'validé' ? 'Validé' : 'En attente'}
                                                    </span>
                                                </div>
                                                <p className="comment-product-line" style={{ margin: 0, fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                                                    <ShoppingCart size={10} style={{ verticalAlign: 'middle', marginRight: '4px' }}/>
                                                    {com.climatiseurNom}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {/* Rating Badge */}
                                        <span className="comment-rating-badge" style={{ fontSize: '0.9rem', color: 'var(--admin-secondary)', fontWeight: '800', background: 'rgba(112, 0, 0, 0.05)', padding: '5px 12px', borderRadius: '10px', height: 'fit-content' }}>
                                            <span className="rating-full-text">{com.avis}</span>
                                            <span className="rating-short-text" style={{ display: 'none', alignItems: 'center', gap: '4px' }}>
                                                {parseFloat(com.avis) || com.avis?.charAt(0) || 5} <Star size={12} fill="#eab308" color="#eab308" />
                                            </span>
                                        </span>
                                    </div>
                                    
                                    <p className="comment-body-text" style={{ margin: '0 0 20px 0', fontSize: '0.95rem', color: 'var(--admin-text-main)', lineHeight: '1.7', padding: '0 5px', wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                        {com.commantaire}
                                    </p>

                                    <div className="comment-card-footer-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid #f8fafc' }}>
                                        <div className="comment-stats-group" style={{ display: 'flex', gap: '20px', color: 'var(--admin-text-muted)', fontSize: '0.8rem' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Heart size={14} fill={com.like > 0 ? "var(--admin-secondary)" : "none"} color="var(--admin-secondary)" /> {com.like} Likes</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><MessageCircle size={14} /> ID: #{com.id}</span>
                                        </div>
                                        <div className="comment-actions-and-mobile-status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                            <div className="action-btns">
                                                <button 
                                                    className={`btn-icon ${com.status === 'validé' ? 'active-success' : ''}`} 
                                                    title={com.status === 'validé' ? "Déjà validé" : "Valider"} 
                                                    onClick={() => handleValider(com.climatiseurId, com.id, com.status)}
                                                    style={{ 
                                                        color: com.status === 'validé' ? 'var(--admin-success)' : 'var(--admin-text-muted)',
                                                        cursor: com.status === 'validé' ? 'default' : 'pointer',
                                                        opacity: com.status === 'validé' ? 0.7 : 1
                                                    }}
                                                    disabled={com.status === 'validé'}
                                                >
                                                    <CheckCircle size={20} />
                                                </button>
                                                <button 
                                                    className="btn-icon btn-delete" 
                                                    title="Supprimer" 
                                                    onClick={() => handleSupprimer(com.climatiseurId, com.id)}
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            </div>
                                            <span className="comment-status-badge mobile-status-badge" style={{ 
                                                display: 'none',
                                                fontSize: '0.65rem', 
                                                fontWeight: '900', 
                                                padding: '3px 8px', 
                                                borderRadius: '6px',
                                                background: com.status === 'validé' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                                color: com.status === 'validé' ? '#166534' : '#9a3412',
                                                textTransform: 'uppercase'
                                            }}>
                                                {com.status === 'validé' ? 'Validé' : 'En attente'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--admin-text-muted)', background: 'white', borderRadius: '20px', border: '1px dashed var(--admin-border)' }}>
                                <MessageSquare size={48} style={{ opacity: 0.1, marginBottom: '20px' }} />
                                <p style={{ fontWeight: '600' }}>Aucun commentaire trouvé pour cette sélection.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Add Comment Modal (Admin Reply) */}
                {isAddModalOpen && (
                    <div className="admin-modal-overlay" onClick={() => setIsAddModalOpen(false)}>
                        <div className="admin-modal-content add-comment-modal" style={{ maxWidth: '650px' }} onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><MessageSquare /> Nouveau commentaire</h2>
                                <button className="modal-close-btn" onClick={() => setIsAddModalOpen(false)}>&times;</button>
                            </div>
                            <form className="modal-body" onSubmit={handleAddComment}>
                                <div className="form-group">
                                    <label>Produit concerné</label>
                                    <select 
                                        className="admin-select" 
                                        value={newAdminComment.climatiseurId}
                                        onChange={e => setNewAdminComment({...newAdminComment, climatiseurId: e.target.value})}
                                        required
                                    >
                                        <option value="">Choisir un climatiseur...</option>
                                        {listC.map(c => <option key={c.id} value={c.id}>{c.nomClimatisseur}</option>)}
                                    </select>
                                </div>

                                <div className="form-group-row" style={{ marginTop: '5px' }}>
                                    <div className="form-group">
                                        <label>Nom de l'auteur</label>
                                        <input type="text" value={newAdminComment.username} readOnly required style={{ background: '#f8fafc', cursor: 'not-allowed', color: 'var(--admin-text-muted)' }} />
                                    </div>
                                    <div className="form-group">
                                        <label>Note / Avis</label>
                                        <select className="admin-select" value={newAdminComment.avis} onChange={e => setNewAdminComment({...newAdminComment, avis: e.target.value})}>
                                            <option value="5">⭐⭐⭐⭐⭐ (Excellent)</option>
                                            <option value="4">⭐⭐⭐⭐ (Très bien)</option>
                                            <option value="3">⭐⭐⭐ (Bien)</option>
                                            <option value="2">⭐⭐ (Passable)</option>
                                            <option value="1">⭐ (Mauvais)</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Message</label>
                                    <textarea 
                                        rows="4" 
                                        placeholder="Saisissez votre réponse ou avis..." 
                                        value={newAdminComment.commantaire} 
                                        onChange={e => setNewAdminComment({...newAdminComment, commantaire: e.target.value})} 
                                        required
                                    />
                                </div>
                                <button type="submit" className="admin-btn-save" style={{ width: '100%', justifyContent: 'center', opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }} disabled={isSubmitting}>
                                    {isSubmitting ? "Publication en cours..." : "Publier le commentaire"}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Confirmation Delete Modal */}
                {confirmDeleteComment && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                        <style>{`
                            @keyframes shakeError {
                                0%, 100% { transform: translateX(0); }
                                20%, 60% { transform: translateX(-5px); }
                                40%, 80% { transform: translateX(5px); }
                            }
                        `}</style>
                        <div style={{ background: 'var(--admin-card-bg)', borderRadius: '16px', padding: '36px', maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid var(--admin-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Trash2 size={22} color="var(--admin-danger)" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--admin-text-main)', fontWeight: 700 }}>Supprimer le commentaire</h3>
                                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--admin-text-muted)' }}>Cette action est irréversible.</p>
                                </div>
                            </div>
                            
                            {deleteError && (
                                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.85rem', fontWeight: 600, animation: 'shakeError 0.4s ease-in-out', border: '1px solid #fca5a5' }}>
                                    {deleteError}
                                </div>
                            )}

                            <p style={{ fontSize: '0.9rem', color: 'var(--admin-text-main)', marginBottom: '24px', lineHeight: 1.6 }}>
                                Êtes-vous sûr de vouloir supprimer le commentaire de <strong>{confirmDeleteComment.username}</strong> sur le produit <strong>{confirmDeleteComment.climatiseurNom}</strong> ?
                            </p>
                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <button onClick={() => { setConfirmDeleteComment(null); setDeleteError(''); }} style={{ padding: '10px 22px', borderRadius: '8px', border: '1px solid var(--admin-border)', background: 'transparent', color: 'var(--admin-text-main)', cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
                                <button onClick={confirmDeleteAction} style={{ padding: '10px 22px', borderRadius: '8px', border: 'none', background: 'var(--admin-danger)', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>Supprimer définitivement</button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
